import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Video, Undo2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import AdminNav from "@/components/AdminNav";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];

const STATUS_COLOR = {
  pending: "#B85C38",
  confirmed: "#7D8C60",
  completed: "#1A1A1A",
  cancelled: "#a3a3a3",
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [meRes, appts] = await Promise.all([api.get("/auth/me"), api.get("/appointments")]);
      setMe(meRes.data);
      setRows(appts.data);
    } catch (e) {
      toast.error("Session expired");
      localStorage.removeItem("admin_token");
      nav("/admin/login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    localStorage.removeItem("admin_token");
    nav("/admin/login");
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/appointments/${id}/status`, { status });
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error("Could not update status");
    }
  };

  const [refunding, setRefunding] = useState(null); // appt row or null (for dialog)
  const [refundBusy, setRefundBusy] = useState(false);
  const [price, setPrice] = useState(500);

  useEffect(() => {
    api.get("/config").then((r) => {
      const p = r.data?.consultation_price_inr;
      if (typeof p === "number") setPrice(p);
    }).catch(() => {});
  }, []);

  const doRefund = async () => {
    if (!refunding) return;
    setRefundBusy(true);
    try {
      const { data } = await api.post(`/appointments/${refunding.id}/refund`);
      setRows((prev) => prev.map((r) => r.id === refunding.id
        ? { ...r, payment_status: "refunded", status: "cancelled", refund_id: data.refund_id }
        : r
      ));
      toast.success(`Refunded ₹${data.amount?.toFixed?.(0) || "500"}`);
      setRefunding(null);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Refund failed");
      setRefunding(null);
    } finally {
      setRefundBusy(false);
    }
  };

  const counts = STATUSES.reduce((acc, s) => {
    acc[s] = rows.filter((r) => r.status === s).length;
    return acc;
  }, {});
  const paidTotal = rows.filter((r) => r.payment_status === "paid").length * price;
  const refundedTotal = rows.filter((r) => r.payment_status === "refunded").length * price;

  return (
    <div style={{ background: "var(--paper)", minHeight: "100vh" }} data-testid={ADMIN.dashboard}>
      <AdminNav meName={me?.name} onRefresh={load} />

      <main className="max-w-[1600px] mx-auto px-6 md:px-10 py-10">
        {/* Stat cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10"
        >
          <StatCard label="Total" value={rows.length} accent="var(--ink)" />
          {STATUSES.map((s) => (
            <StatCard key={s} label={s} value={counts[s] || 0} accent={STATUS_COLOR[s]} />
          ))}
        </motion.div>

        {/* Revenue callout */}
        <div className="mb-10 grid grid-cols-12 gap-4">
          <div
            className="col-span-12 md:col-span-5 p-8 border hairline"
            style={{ background: "var(--ink)", color: "var(--cream)" }}
          >
            <div className="mono-eyebrow" style={{ color: "#a3a3a3" }}>Received this practice</div>
            <div className="serif text-6xl md:text-7xl mt-3" style={{ letterSpacing: "-0.03em" }}>
              ₹{(paidTotal - refundedTotal).toLocaleString("en-IN")}
            </div>
            <div className="mono-eyebrow mt-3" style={{ color: "#a3a3a3" }}>
              From {rows.filter((r) => r.payment_status === "paid").length} paid · {rows.filter((r) => r.payment_status === "refunded").length} refunded
            </div>
          </div>
          <div className="col-span-12 md:col-span-7 p-8 border hairline" style={{ background: "#fff" }}>
            <div className="mono-eyebrow">Notes for the practitioner</div>
            <p className="serif text-2xl md:text-3xl mt-3 leading-tight" style={{ letterSpacing: "-0.015em" }}>
              "The day's schedule sits below. Mark a session confirmed once you've replied by phone, and completed once it ends."
            </p>
          </div>
        </div>

        {/* Table */}
        <section className="border hairline" style={{ background: "#fff" }}>
          <div className="border-b hairline p-5 flex items-center justify-between">
            <div className="serif text-2xl">All appointments</div>
            <div className="mono-eyebrow">{rows.length} total</div>
          </div>

          {loading && <div className="p-10 mono-eyebrow">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="p-16 text-center">
              <div className="serif text-3xl">No appointments yet.</div>
              <p className="mt-3" style={{ color: "var(--mute)" }}>They'll appear here as they come in.</p>
            </div>
          )}

          {!loading && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left" style={{ background: "var(--cream-2)" }}>
                    {["Name", "Contact", "Topic", "When", "Mode", "Payment", "Status", ""].map((h) => (
                      <th key={h} className="mono-eyebrow p-4">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr
                      key={r.id}
                      data-testid={`${ADMIN.appointmentRow}${i}`}
                      className="border-t hairline"
                    >
                      <td className="p-4">
                        <div className="serif text-lg" style={{ letterSpacing: "-0.01em" }}>{r.name}</div>
                        {r.notes && <div className="mt-1 text-xs" style={{ color: "var(--mute)" }}>{r.notes.slice(0, 80)}{r.notes.length > 80 && "…"}</div>}
                      </td>
                      <td className="p-4">
                        <div>{r.email}</div>
                        <div className="text-xs" style={{ color: "var(--mute)" }}>{r.phone}</div>
                      </td>
                      <td className="p-4 capitalize">{r.topic}</td>
                      <td className="p-4">
                        <div>{r.date}</div>
                        <div className="text-xs" style={{ color: "var(--mute)" }}>{r.time_slot}</div>
                      </td>
                      <td className="p-4 capitalize">{r.mode}</td>
                      <td className="p-4">
                        <span
                          className="inline-block px-2 py-1 text-[10px] tracking-wider uppercase"
                          style={{
                            background:
                              r.payment_status === "paid" ? "var(--olive)" :
                              r.payment_status === "refunded" ? "#a3a3a3" :
                              "var(--line)",
                            color:
                              r.payment_status === "paid" || r.payment_status === "refunded"
                                ? "#fff" : "var(--ink)",
                          }}
                          data-testid={`admin-payment-status-${i}`}
                        >
                          {r.payment_status}
                        </span>
                      </td>
                      <td className="p-4">
                        <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                          <SelectTrigger data-testid={`${ADMIN.statusSelect}${i}`} className="w-[140px] rounded-none border-black">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col items-start gap-2">
                          {r.mode === "online" && (
                            <a
                              href={`/meeting/${r.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="link-underline text-xs inline-flex items-center gap-1"
                            >
                              <Video size={12} /> Meeting
                            </a>
                          )}
                          {r.payment_status === "paid" && (
                            <button
                              onClick={() => setRefunding(r)}
                              data-testid={`admin-refund-btn-${i}`}
                              className="link-underline text-xs inline-flex items-center gap-1"
                              style={{ color: "var(--terra)" }}
                            >
                              <Undo2 size={12} /> Refund
                            </button>
                          )}
                          {r.payment_status === "refunded" && (
                            <span className="mono-eyebrow" style={{ color: "var(--mute)", fontSize: 10 }}>
                              Refunded
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      <AlertDialog open={!!refunding} onOpenChange={(open) => !open && setRefunding(null)}>
        <AlertDialogContent className="rounded-none border-black" data-testid="refund-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="serif text-3xl" style={{ letterSpacing: "-0.015em" }}>
              Refund this session?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm mt-2" style={{ color: "var(--ink-2)" }}>
              {refunding && (
                <>
                  You are about to refund <strong style={{ color: "var(--ink)" }}>₹{price}</strong> to{" "}
                  <strong style={{ color: "var(--ink)" }}>{refunding.name}</strong> for their{" "}
                  {refunding.topic} session on {refunding.date} · {refunding.time_slot}.
                  <br /><br />
                  The appointment will be <strong>cancelled</strong> and a confirmation email will
                  be sent. Refunds usually reach the customer's card in 5–10 business days. This
                  cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel
              className="rounded-none border-black"
              data-testid="refund-cancel-btn"
              disabled={refundBusy}
            >
              Never mind
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                doRefund();
              }}
              disabled={refundBusy}
              className="rounded-none"
              style={{ background: "var(--terra)", color: "#fff" }}
              data-testid="refund-confirm-btn"
            >
              {refundBusy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Undo2 size={14} className="mr-2" />}
              {refundBusy ? "Refunding…" : `Yes, refund ₹${price}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="p-5 border hairline" style={{ background: "#fff" }}>
      <div className="mono-eyebrow" style={{ color: accent }}>{label}</div>
      <div className="serif text-4xl md:text-5xl mt-3" style={{ letterSpacing: "-0.02em" }}>
        {value}
      </div>
    </div>
  );
}
